import {
  ArrowLeft,
  CheckmarkFilled,
  DataBase,
  Help,
  Redo,
  RuleDraft,
} from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { Button, Tooltip } from 'vet-bones/design-system/components';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { TEST_IDS } from 'src/constants/testIds';
import { CreateProjectStep } from 'src/containers/CreateProjectOverlay/CreateProjectStep';
import { useCreateProjectBase } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectBase';
import {
  CreateProjectStepValue,
  useCreateProjectState,
} from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import * as copy from 'src/templates/copy';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';

export const CreateProjectSteps: React.VFC = () => {
  const createProject = useCreateProjectBase();
  const {
    currentStep,
    name,
    didGenerateInitialQuestions,
    isGeneratingQuestions,
    isAutoGeneratingModel,
    isAutoGeneratingMockData,
    handleBack,
    handleGenerateQuestions,
    setCurrentStep,
  } = useCreateProjectState();
  const isGenerating = isAutoGeneratingModel || isAutoGeneratingMockData;
  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );
  const steps = React.useMemo(
    () =>
      [
        {
          icon: RuleDraft,
          text: copy.components.createProjectOverlay.steps.projectScoping,
          value: CreateProjectStepValue.PROJECT_SCOPING,
          onClick: () => setCurrentStep(CreateProjectStepValue.PROJECT_SCOPING),
        },
      ]
        .concat(
          useVoiceboxSuggestions
            ? [
                {
                  icon: Help,
                  text: copy.components.createProjectOverlay.steps
                    .competencyQuestions,
                  value: CreateProjectStepValue.COMPETENCY_QUESTIONS,
                  onClick: () => {
                    if (!didGenerateInitialQuestions) {
                      handleGenerateQuestions();
                    }
                    setCurrentStep(CreateProjectStepValue.COMPETENCY_QUESTIONS);
                  },
                },
              ]
            : []
        )
        .concat(
          useVoiceboxSuggestions
            ? [
                {
                  icon: DataBase,
                  text: copy.components.createProjectOverlay.steps
                    .dataConnection,
                  value: CreateProjectStepValue.DATA_CONNECTION,
                  onClick: () => {
                    setCurrentStep(CreateProjectStepValue.DATA_CONNECTION);
                  },
                },
              ]
            : []
        ),
    [
      didGenerateInitialQuestions,
      handleGenerateQuestions,
      setCurrentStep,
      useVoiceboxSuggestions,
    ]
  );
  const currentIdx = steps.findIndex((step) => step.value === currentStep);

  const showStepButtons = steps.length > 1;

  return (
    <div
      className={classNames('sd-create-project-steps', {
        'sd-create-project-steps--no-steps': !showStepButtons,
      })}
    >
      <CreateProjectStep
        disabled={isGenerating}
        icon={ArrowLeft}
        iconSize="lg"
        onClick={isGenerating ? undefined : handleBack}
        rightBorder={currentIdx === 0}
        testId={TEST_IDS.createProjectOverlay.steps.backButton}
      />
      {showStepButtons
        ? steps.map(({ icon, onClick, text, value }, stepIdx) => {
            return (
              <CreateProjectStep
                key={value}
                active={stepIdx === currentIdx}
                disabled={
                  isGenerating || (!name.trim() && stepIdx > currentIdx)
                }
                icon={stepIdx < currentIdx ? CheckmarkFilled : icon}
                leftBorder={stepIdx === currentIdx + 1}
                onClick={
                  isGenerating || stepIdx === currentIdx ? undefined : onClick
                }
                rightBorder={stepIdx === currentIdx - 1}
                stepIdx={`0${stepIdx + 1}`}
                testId={TEST_IDS.createProjectOverlay.steps.stepButton(
                  value,
                  stepIdx
                )}
                text={text}
              />
            );
          })
        : null}
      <CreateProjectStep leftBorder={currentIdx === steps.length - 1}>
        <Tooltip
          description={
            copy.components.createProjectOverlay.projectScopingPanel
              .titleRequiredTooltip
          }
          disabled={Boolean(name.trim())}
        >
          <Button
            disabled={isGenerating || isGeneratingQuestions || !name.trim()}
            onClick={() => createProject()}
            rightIcon={Redo}
            size="md"
            testId={TEST_IDS.createProjectOverlay.steps.submitButton}
            variant="tertiary-accent"
          >
            {copy.components.createProjectOverlay.steps.createProject}
          </Button>
        </Tooltip>
      </CreateProjectStep>
    </div>
  );
};
